import React, { useState, useEffect } from 'react';

export interface SvgLoaderProps {
  name: string;
  className?: string;
  width?: number;
  height?: number;
}

export const useSvgLoader = (iconName: string) => {
  const [svgContent, setSvgContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSvg = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/Icons/${iconName}.svg`);
        if (!response.ok) {
          throw new Error(`Failed to load SVG: ${iconName}`);
        }
        const content = await response.text();
        setSvgContent(content);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load SVG');
      } finally {
        setLoading(false);
      }
    };

    loadSvg();
  }, [iconName]);

  return { svgContent, loading, error };
};

export const SvgLoader: React.FC<SvgLoaderProps> = ({ 
  name, 
  className = '', 
  width = 24, 
  height = 24 
}) => {
  const { svgContent, loading, error } = useSvgLoader(name);

  if (loading) {
    return (
      <div 
        className={`animate-pulse bg-gray-200 rounded ${className}`}
        style={{ width, height }}
      />
    );
  }

  if (error) {
    return (
      <div 
        className={`bg-red-100 text-red-500 rounded flex items-center justify-center ${className}`}
        style={{ width, height }}
      >
        ?
      </div>
    );
  }

  return (
    <div 
      className={className}
      style={{ width, height }}
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  );
};